import React, { Suspense } from 'react';
import PropTypes from 'prop-types';

import { styled } from '@mui/material';

import { Outlet } from 'react-router-dom';

import {
  AppBar,
  AppBarSlot,
  Avatar,
  Box,
  Button,
  Divider,
  Drawer,
  DrawerBody,
  DrawerHeader,
  Grid,
  LinearProgress,
  Link,
  Sidebar,
  SidebarBody,
  SidebarFooter,
  SidebarFooterCard,
  SidebarHeader,
  SidebarHeaderCard,
  Stack,
} from '@datanomik/commons-ui';

import {
  CloseLine,
  Datanomik,
  Menu3Line,
  MenuFoldFill,
  MenuUnfoldFill,
} from '@datanomik/commons-ui/icons';

import { FORBIDDEN_ERROR } from '@/assets/AppConstants';

import { Can } from '@/contexts/ability';
import { useAppContext } from '@/contexts/app';

import MainMenu from '@/views/base/MainMenu';
import Forbidden from '@/views/base/Forbidden';
import Breadcrumbs from '@/views/base/Breadcrumbs';
import NotificationsButton from '@/views/base/NotificationsButton';

import ClientButton from '@/views/common/ClientButton';
import WorkspaceButton from '@/views/common/WorkspaceButton';
import { SupportMenuItem } from '@/features/support/support-button/SupportMenuItem';
import useDashboard from './hooks';
import styles from './styles';

const DashboardLayoutRaw = ({ className }) => {
  const {
    open,
    user,
    isMobile,
    defaultWorkspace,
    sidebarCollapsed,
    anchorElClient,
    anchorElClientMobile,
    anchorElWorkspace,
    anchorElWorkspaceMobile,
    handleClickClient,
    handleClickClientMobile,
    handleClickWorkspace,
    handleClickWorkspaceMobile,
    handleCloseMenu,
    toggleDrawer,
    handleLogout,
    isOnboarding,
    toggleSidebarCollapsed,
  } = useDashboard();

  const { requestStatus, isLoading } = useAppContext();
  const isForbidden = requestStatus === FORBIDDEN_ERROR;

  let marginLeft = isMobile ? undefined : ' 272px';
  if (sidebarCollapsed) marginLeft = '80px';

  return (
    <Box className={className} sx={{
      height: '100vh',
    }}>
      <Grid container width="100%" height="100%">
        {!isMobile && (
          <Grid
            id="App-Slot-Sidebar-Navigation"
            size="auto"
            sx={{
              position: 'fixed',
              '& + *': {
                transition: 'all 0.25s ease-in-out 0s',
                marginLeft,
              },
            }}
            zIndex={200}
          >
            <Sidebar
              collapsed={sidebarCollapsed}
              open={Boolean(anchorElClient) || Boolean(anchorElWorkspace)}
            >
              <SidebarHeader divider={false}>
                <SidebarHeaderCard
                  startAdornment={
                    <Datanomik color="primary" sx={{ fontSize: '40px' }} />
                  }
                  endAdornment={(
                    <WorkspaceButton
                      open={Boolean(anchorElWorkspace)}
                      anchorEl={anchorElWorkspace}
                      handleClose={handleCloseMenu}
                      isMobile={isMobile}
                    />
                  )}
                  onClick={handleClickWorkspace}
                  text="Datanomik"
                  subText={defaultWorkspace?.name}
                  sx={{
                    cursor: 'pointer',
                  }}
                />
              </SidebarHeader>

              <Divider textAlign="center" sx={{ mt: '-10px' }}>
                <Button
                  onClick={toggleSidebarCollapsed(false)}
                  startIcon={
                    sidebarCollapsed ? <MenuUnfoldFill /> : <MenuFoldFill />
                  }
                  size="xxsmall"
                  color="black"
                  variant="text"
                />
              </Divider>

              <SidebarBody>
                {!isOnboarding && (
                  <>
                    <MainMenu />
                    <Stack sx={{ marginTop: 'auto' }}>
                      <SupportMenuItem />
                      <MainMenu isSettings />
                    </Stack>
                  </>
                )}
              </SidebarBody>

              <SidebarFooter>
                <SidebarFooterCard
                  startAdornment={
                    <Avatar>{`${user?.firstName?.[0]}${user?.lastName?.[0]}`}</Avatar>
                  }
                  endAdornment={(
                    <ClientButton
                      open={Boolean(anchorElClient)}
                      anchorEl={anchorElClient}
                      handleClose={handleCloseMenu}
                      handleLoogut={handleLogout}
                      isMobile={isMobile}
                    />
                  )}
                  onClick={handleClickClient}
                  text={`${user?.firstName} ${user?.lastName}`}
                  subText={user?.clientName}
                  sx={{
                    cursor: 'pointer',
                  }}
                />
              </SidebarFooter>
            </Sidebar>
          </Grid>
        )}

        <Grid
          id="App-Slot-Sidebar-Menu"
          size="auto"
          sx={{
            position: 'sticky',
            top: '0',
            height: '100vh',
            zIndex: 100,
          }}
        />

        <Grid
          id="App-Slot-Page"
          size="grow"
          sx={{
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            minWidth: 0,
            height: '100%',
          }}
        >
          {!isOnboarding && (
            <AppBar sx={{
              padding: isMobile ? '15px' : '20px 32px',
              gap: isMobile ? '15px' : '20px',
              zIndex: 100,
              '.DnkAppBarSlot-root a > .DnkSvgIcon-root': { fontSize: '36px' },
            }}
            >
              {isMobile ? (
                <>
                  <AppBarSlot name="top">
                    <Link to="/">
                      <Datanomik color="primary" sx={{ fontSize: '20px !important' }} />
                    </Link>
                    <AppBarSlot
                      name="top-right-mobile"
                      sx={{
                        marginLeft: 'auto',
                        flexDirection: 'row-reverse',
                      }}
                    >
                      <Button
                        variant="outlined"
                        color="black"
                        onClick={toggleDrawer(true)}
                        startIcon={<Menu3Line />}
                        size="small"
                      />
                    </AppBarSlot>
                  </AppBarSlot>
                  <AppBarSlot
                    sx={{
                      marginBottom: '-17px',
                      maxWidth: '1440px',
                      width: '100%',
                      marginLeft: 'auto',
                      marginRight: 'auto',
                    }}
                    name="bottom"
                  >
                    <AppBarSlot
                      name="bottom-tabs-mobile"
                      sx={{
                        width: '100%',
                        '.dn-tabs-wrapper': {
                          width: '100%',
                        },
                      }}
                    />
                  </AppBarSlot>
                </>
              ) : (
                <>
                  <AppBarSlot
                    sx={{
                      height: '47.5px',
                      maxWidth: '1440px',
                      width: '100%',
                      marginLeft: 'auto',
                      marginRight: 'auto',
                    }}
                    name="top"
                  >
                    <AppBarSlot name="top-left" sx={{ marginRight: 'auto' }}>
                      <Breadcrumbs />
                    </AppBarSlot>
                    <AppBarSlot name="top-middle" sx={{ margin: '0 auto' }} />
                    <AppBarSlot name="top-right" sx={{ marginLeft: 'auto' }}>
                      <Can I="view" a="settings-notifications">
                        <NotificationsButton />
                      </Can>
                    </AppBarSlot>
                  </AppBarSlot>
                  <AppBarSlot
                    sx={{
                      marginBottom: '-22px',
                      maxWidth: '1440px',
                      width: '100%',
                      marginLeft: 'auto',
                      marginRight: 'auto',
                    }}
                    name="bottom"
                  >
                    <AppBarSlot name="bottom-tabs-desktop" sx={{ marginRight: 'auto' }} />
                    <AppBarSlot name="bottom-button-desktop" sx={{ marginLeft: 'auto' }} />
                  </AppBarSlot>
                  <AppBarSlot
                    sx={{
                      maxWidth: '1440px',
                      width: '100%',
                      marginLeft: 'auto',
                      marginRight: 'auto',
                    }}
                    name="bottom-footer"
                  />
                </>
              )}

              <LinearProgress sx={{
                opacity: isLoading.page ? 1 : 0,
                zIndex: 1000,
                height: '2px',
                marginBottom: isMobile ? '-15px' : '-20px',
                marginLeft: isMobile ? '-15px' : '-32px',
                marginRight: isMobile ? '-15px' : '-32px',
              }}
              />
            </AppBar>
          )}

          <Stack component="main" height="100%">
            <Box className="dn-container" height="100%">
              <Suspense>
                <Outlet />
              </Suspense>
            </Box>
          </Stack>
        </Grid>
      </Grid>

      <Drawer
        open={open}
        onClose={toggleDrawer(false)}
        anchor="right"
        keepMounted
      >
        <DrawerHeader
          endAdornment={(
            <Button
              onClick={toggleDrawer(false)}
              color="black"
              startIcon={<CloseLine />}
              size="xsmall"
              variant="text"
            />
          )}
        />
        <DrawerBody>
          <Sidebar width="100%" height="100%">
            <SidebarHeader>
              <SidebarHeaderCard
                startAdornment={
                  <Datanomik color="primary" sx={{ fontSize: '40px' }} />
                }
                endAdornment={(
                  <WorkspaceButton
                    open={Boolean(anchorElWorkspaceMobile)}
                    anchorEl={anchorElWorkspaceMobile}
                    handleClose={handleCloseMenu}
                    isMobile={isMobile}
                  />
                )}
                onClick={handleClickWorkspaceMobile}
                text="Datanomik"
                subText={defaultWorkspace?.name}
                sx={{
                  cursor: 'pointer',
                }}
              />
            </SidebarHeader>

            <SidebarBody>
              {/* {!isOnboarding && (
                <>
                  <MainMenu />
                  <MainMenu isSettings sx={{ marginTop: 'auto' }} />
                </>
              )} */}
            </SidebarBody>

            <SidebarFooter>
              <SidebarFooterCard
                startAdornment={
                  <Avatar>{`${user?.firstName?.[0]}${user?.lastName?.[0]}`}</Avatar>
                }
                endAdornment={(
                  <ClientButton
                    open={Boolean(anchorElClientMobile)}
                    anchorEl={anchorElClientMobile}
                    handleClose={handleCloseMenu}
                    handleLoogut={handleLogout}
                    isMobile={isMobile}
                  />
                )}
                onClick={handleClickClientMobile}
                text={`${user?.firstName} ${user?.lastName}`}
                subText={user?.clientName}
                sx={{
                  cursor: 'pointer',
                }}
              />
            </SidebarFooter>
          </Sidebar>
        </DrawerBody>
      </Drawer>

      {isForbidden && (
        <Forbidden />
      )}
    </Box>

  );
};

DashboardLayoutRaw.propTypes = {
  className: PropTypes.string,
};

export const DashboardLayout = styled(DashboardLayoutRaw)(styles);
